import { MenuItem, PopoverPosition, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { MultiSelect2 } from '@blueprintjs/select';
import * as React from 'react';
import { isEqual } from 'vet-bones/utils';
import { SelectItem } from 'vet-bones/utils/select';

import { TEST_IDS, getClassItemTestId } from 'src/constants/testIds';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { projectClassIrisSelector } from 'src/selectors/projectClassIris';
import { projectClassItemsSelector } from 'src/selectors/projectClassItems';
import { projectLabelsByIriSelector } from 'src/selectors/projectLabelsByIri';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { toggleMultiSelectItemId } from 'src/utils/select/toggleMultiSelectItemId';

export type ClassSelectorProps = {
  popoverPosition?: PopoverPosition;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
};

const classPredicate = (search: string, item: SelectItem) =>
  item.text.toLowerCase().includes(search.toLowerCase());

export const ClassSelector: React.VFC<ClassSelectorProps> = ({
  popoverPosition = Position.BOTTOM_LEFT,
  selected,
  setSelected,
}) => {
  const { projectId } = useRouterParams();

  const labelsByIri = useReduxSelector(
    projectLabelsByIriSelector(projectId),
    isEqual
  );

  const classItems = useReduxSelector(projectClassItemsSelector(projectId));
  const projectClassIris = useReduxSelector(
    projectClassIrisSelector(projectId)
  );

  const selectedItems = React.useMemo(
    () =>
      (selected || [])
        .filter((iri) => projectClassIris.has(iri))
        .map((iri) => {
          return {
            text: getLabelForIri(labelsByIri, iri),
            value: iri,
          };
        }),
    [labelsByIri, projectClassIris, selected]
  );

  return (
    <div data-testid={TEST_IDS.class.selector}>
      <MultiSelect2<SelectItem>
        fill
        itemPredicate={classPredicate}
        itemRenderer={(
          { text, value },
          { handleClick, modifiers: { active, disabled, matchesPredicate } }
        ) => {
          if (!matchesPredicate) {
            return null;
          }
          return (
            <MenuItem
              key={value}
              active={active}
              data-testid={getClassItemTestId(value)}
              disabled={disabled}
              icon={selected.includes(value) ? IconNames.TICK : IconNames.BLANK}
              onClick={handleClick}
              shouldDismissPopover={false}
              text={text}
            />
          );
        }}
        items={classItems}
        onItemSelect={({ value }) =>
          setSelected(toggleMultiSelectItemId(value))
        }
        onRemove={(removedClass) =>
          setSelected((prevSelected) =>
            prevSelected.filter((id) => id !== removedClass.value)
          )
        }
        placeholder={copy.components.classSelector.placeholder}
        popoverProps={{
          rootBoundary: 'document',
          minimal: true,
          popoverClassName: 'sd-popover-menu',
          position: popoverPosition,
        }}
        resetOnSelect
        scrollToActiveItem={false}
        selectedItems={selectedItems}
        tagRenderer={({ text }) => text}
      />
    </div>
  );
};
